import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ArrowRight, Factory, RefreshCw } from 'lucide-react';
import { productionBoard, setItemStage } from '../api/resources';
import { useToast } from '../context/ToastContext';
import { fmtDate } from '../utils/format';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import { STAGE_LABEL } from '../components/invoice/StageControl';

// A colour per stage, used on the card, the flow bar and every chip, so one
// glance is enough to tell where a garment is.
const TONE = {
  RECEIVED: { dot: 'bg-slate-500', ring: 'border-slate-400', chip: 'bg-slate-500/10 text-content-muted' },
  CUTTING: { dot: 'bg-info-500', ring: 'border-info-500', chip: 'bg-info-50 text-info-700' },
  CUTTING_DONE: { dot: 'bg-info-600', ring: 'border-info-600', chip: 'bg-info-50 text-info-700' },
  ASSIGNED: { dot: 'bg-brand-400', ring: 'border-brand-400', chip: 'bg-brand-50 text-brand-700' },
  STITCHING: { dot: 'bg-brand-600', ring: 'border-brand-600', chip: 'bg-brand-50 text-brand-700' },
  QC: { dot: 'bg-warning-500', ring: 'border-warning-500', chip: 'bg-warning-50 text-warning-700' },
  ALTERATION: { dot: 'bg-danger-500', ring: 'border-danger-500', chip: 'bg-danger-50 text-danger-600' },
  READY: { dot: 'bg-success-500', ring: 'border-success-500', chip: 'bg-success-50 text-success-700' },
  DELIVERED: { dot: 'bg-slate-400', ring: 'border-slate-400', chip: 'bg-surface-sunken text-content-subtle' },
};
// The moves a person makes from here; cutting and stitching follow the workers.
const MOVES = {
  RECEIVED: ['QC'],
  CUTTING: ['QC'],
  CUTTING_DONE: ['QC'],
  ASSIGNED: ['QC'],
  STITCHING: ['QC'],
  QC: ['ALTERATION', 'READY'],
  ALTERATION: ['QC', 'READY'],
  READY: ['DELIVERED', 'ALTERATION'],
  DELIVERED: [],
};

/**
 * The production pipeline on its own page: big stage cards left to right, and
 * under them the garments at the stage you pick — with the buttons that move
 * them on.
 *
 * Deliberately roomy. The owner reads this across the workshop, not up close,
 * so counts are large, colours carry the meaning, and anything late is red.
 */
export default function ProductionPipeline() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [picked, setPicked] = useState(null); // stage whose garments are listed
  const [view, setView] = useState('stage'); // 'stage' | 'overdue' | 'today' | 'tomorrow'

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['production-board'],
    queryFn: productionBoard,
    refetchInterval: 60000,
  });

  const move = useMutation({
    mutationFn: ({ itemId, stage }) => setItemStage(itemId, { stage }),
    onSuccess: (d) => {
      success(d.message || 'Moved on.');
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not move the garment.'),
  });

  const rows = useMemo(() => {
    if (!data) return [];
    // A date tile lists by date; a stage card lists every garment at that
    // stage, whenever it is due — that is what the count on the card means.
    if (view !== 'stage') return data.due[view] || [];
    return picked ? (data.items || []).filter((r) => r.stage === picked) : [];
  }, [data, view, picked]);

  if (isLoading) return <Spinner label="Reading the floor…" />;
  if (isError || !data) {
    return <div className="card p-10 text-center text-sm text-content-muted">Could not load the production pipeline.</div>;
  }

  const { stages = [], due, onFloor } = data;
  const live = stages.filter((s) => s.key !== 'DELIVERED');
  const total = live.reduce((a, b) => a + b.count, 0) || 1;
  const busiest = live.reduce((m, s) => (s.count > (m?.count || 0) ? s : m), null);

  const listTitle = view === 'stage'
    ? (picked ? `${STAGE_LABEL[picked]} — every garment here` : null)
    : { overdue: 'Overdue garments', today: 'Due today', tomorrow: 'Due tomorrow' }[view];

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Factory}
        title="Production Pipeline"
        subtitle={`${onFloor} garment${onFloor === 1 ? '' : 's'} on the floor · cutting and stitching move themselves as workers start and finish`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-outline" onClick={() => refetch()}>
              <RefreshCw size={15} className={isFetching ? 'animate-spin' : ''} /> Refresh
            </button>
            <button className="btn-outline" onClick={() => nav('/invoices')}>
              <ArrowLeft size={15} /> Orders
            </button>
          </div>
        }
      />

      {due.counts.overdue > 0 && (
        <button
          type="button"
          onClick={() => { setView('overdue'); setPicked(null); }}
          className="flex w-full flex-wrap items-center gap-3 rounded-xl border-2 border-danger-500 bg-danger-50 px-5 py-3 text-left hover:brightness-105"
        >
          <AlertTriangle size={20} className="text-danger-600" />
          <span className="text-lg font-bold text-danger-600">
            {due.counts.overdue} garment{due.counts.overdue === 1 ? '' : 's'} past the delivery date
          </span>
          <span className="ml-auto text-sm font-semibold text-danger-600">Show them →</span>
        </button>
      )}

      {/* the pipeline itself */}
      <div>
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
          <h2 className="text-sm font-bold uppercase tracking-wider text-content">The pipeline</h2>
          <span className="text-xs text-content-subtle">Tap a stage to see its garments{busiest?.count ? ` · busiest right now: ${busiest.label}` : ''}</span>
        </div>

        <div className="flex flex-wrap items-stretch gap-2">
          {live.map((s, i) => {
            const on = view === 'stage' && picked === s.key;
            const tone = TONE[s.key];
            return (
              <div key={s.key} className="flex items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => { setView('stage'); setPicked(on ? null : s.key); }}
                  className={`card min-w-[9.5rem] flex-1 border-2 p-4 text-left transition-all hover:shadow-raised ${on ? `${tone.ring} shadow-raised` : 'border-transparent'}`}
                >
                  <span className={`mb-2 block h-2 w-10 rounded-full ${tone.dot}`} />
                  <p className="text-4xl font-bold leading-none tabular-nums text-content">{s.count}</p>
                  <p className="mt-1.5 text-xs font-bold uppercase leading-tight tracking-wide text-content-muted">{s.label}</p>
                  <p className="mt-0.5 text-2xs text-content-subtle">{s.manual ? 'moved by hand' : 'moves by itself'}</p>
                </button>
                {i < live.length - 1 && (
                  <span className="flex items-center text-content-subtle"><ArrowRight size={16} /></span>
                )}
              </div>
            );
          })}
        </div>

        {/* one bar: where the work is piled up */}
        <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-surface-sunken">
          {live.filter((s) => s.count).map((s) => (
            <span key={s.key} className={TONE[s.key].dot} style={{ width: `${(s.count / total) * 100}%` }} title={`${s.label}: ${s.count}`} />
          ))}
        </div>
      </div>

      {/* due buckets as big tiles */}
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ['overdue', 'Overdue', due.counts.overdue, 'border-danger-500 text-danger-600'],
          ['today', 'Due today', due.counts.today, 'border-warning-500 text-warning-700'],
          ['tomorrow', 'Due tomorrow', due.counts.tomorrow, 'border-line text-content'],
        ].map(([key, label, count, cls]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setView(view === key ? 'stage' : key); setPicked(null); }}
            className={`card border-t-4 p-4 text-left transition-shadow hover:shadow-raised ${cls} ${view === key ? 'shadow-raised' : ''}`}
          >
            <p className="text-3xl font-bold tabular-nums">{count}</p>
            <p className="text-xs font-bold uppercase tracking-wide">{label}</p>
          </button>
        ))}
      </div>

      {/* the garments themselves */}
      {(view !== 'stage' || picked) && (
        <section className="card overflow-hidden">
          <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
            <h2 className="text-sm font-bold text-content">{listTitle}</h2>
            <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-2xs font-bold text-content-muted">{rows.length}</span>
            <button className="ml-auto text-xs font-semibold text-brand-700 hover:underline" onClick={() => { setView('stage'); setPicked(null); }}>
              Clear
            </button>
          </header>

          {rows.length === 0 ? (
            <p className="py-10 text-center text-sm italic text-content-subtle">
              {view === 'stage'
                ? 'No garment is at this stage.'
                : 'Nothing due in this window.'}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.itemId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => nav(`/invoices/${r.invoiceId}/edit`, { state: { from: '/production' } })}
                    title="Open the order"
                  >
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <b className="text-base text-content">{r.orderNo}</b>
                      <span className="text-sm text-content-muted">{r.customerName}</span>
                      {r.daysLate > 0 && (
                        <span className="rounded bg-danger-50 px-2 py-0.5 text-2xs font-bold text-danger-600">{r.daysLate} day{r.daysLate === 1 ? '' : 's'} late</span>
                      )}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-content-subtle">
                      <span className="font-semibold text-content-muted">{r.itemName} × {r.qty}</span>
                      <span className={`rounded-full px-2 py-0.5 text-2xs font-bold uppercase ${TONE[r.stage].chip}`}>{r.stageLabel}</span>
                      {r.holder && <span>with {r.holder}</span>}
                      {r.deliveryText && <span>delivery {r.deliveryText}</span>}
                      {r.expectedAt && <span>stage due {fmtDate(r.expectedAt)}</span>}
                    </span>
                  </button>

                  <span className="flex flex-wrap gap-1.5">
                    {(MOVES[r.stage] || []).map((m) => (
                      <button
                        key={m}
                        type="button"
                        className="btn-outline btn-sm"
                        disabled={move.isPending}
                        onClick={() => move.mutate({ itemId: r.itemId, stage: m })}
                      >
                        → {STAGE_LABEL[m]}
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <p className="text-2xs text-content-subtle">
        A garment is due by its order's delivery date. QC, alteration, ready and delivered are moved
        by hand — here, on the order screen, or by the tailor sending work on for checking.
      </p>
    </div>
  );
}
